/**
 * DEV ONLY — dev-app harness for testing `useRefreshRewardsAfterUnlink`.
 *
 * This modal simulates Manulife's Aeroplan unlink modal. It opens when the
 * default `triggerUnlinkAeroplanModal` Masonry action handler in `App.tsx`
 * dispatches the `dev:openUnlinkModal` custom event (i.e. when the user
 * clicks "Unlink Aeroplan® account" in the Rewards Hub).
 *
 * In Manulife's production app they replace the default handler with their
 * own implementation, so this modal only runs in the League dev app. It's
 * useful for verifying the rewards cache-invalidation flow end-to-end
 * without needing access to Manulife's codebase.
 *
 * See the JSDoc on `useRefreshRewardsAfterUnlink` for the full test recipe.
 */
import { useEffect, useState } from 'react';
import {
  Modal,
  HeadingText,
  ParagraphText,
  Button,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useRefreshRewardsAfterUnlink } from '@leagueplatform/manulife-web-extensions';

export const DEV_UNLINK_MODAL_EVENT = 'dev:openUnlinkModal';

export function DevUnlinkModal() {
  const [open, setOpen] = useState(false);
  const refreshRewards = useRefreshRewardsAfterUnlink();

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener(DEV_UNLINK_MODAL_EVENT, handleOpen);
    return () => window.removeEventListener(DEV_UNLINK_MODAL_EVENT, handleOpen);
  }, []);

  const handleUnlink = () => {
    refreshRewards();
    setOpen(false);
  };

  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      <Modal.Content>
        <Modal.Close />
        <StackLayout
          css={{
            padding: '$oneAndHalf',
            paddingBlockStart: '$none',
            gap: '$one',
          }}
        >
          <Modal.Title>
            <HeadingText level="2" size="lg">
              [Dev] Unlink Aeroplan® account?
            </HeadingText>
          </Modal.Title>
          <Modal.Description>
            <ParagraphText>
              This is a dev-only harness for testing the
              <code> useRefreshRewardsAfterUnlink </code>
              hook. Clicking Unlink calls the hook, which invalidates the cached
              Masonry nodes impacted by Aeroplan unlink so the Rewards Hub
              reflects the unlinked state without a page refresh.
            </ParagraphText>
          </Modal.Description>
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="end"
            css={{ gap: '$half', paddingBlockStart: '$one' }}
          >
            <Button priority="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button destructive onClick={handleUnlink}>
              Unlink
            </Button>
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
}
